import fp from 'lodash/fp';
import moment from 'moment';
import isEmpty from './is-empty';

import stringSorter from './stringSorter';
import getEmailAddressTitleForRecipient from './getEmailAddressTitleForRecipient';
import getSelectedForTableSearch from './getSelectedForTableSearch';
import getSort from './getSort';
import { ENUM_PROJECT_STATUS_CONTROL_TYPE } from 'constants/form';
import getFundingApplicationStatus from './getFundingApplicationStatus';

const downloadUrl = process.env.REACT_APP_DOWNLOAD_URL;

export {
  stringSorter,
  getEmailAddressTitleForRecipient,
  isEmpty,
  getSelectedForTableSearch,
  getSort,
  getFundingApplicationStatus,
};

export const isArrayEqual = (arr1 = [], arr2 = [], orderMatter = false) => {
  if (arr1.length === 0 || !arr1) {
    return false;
  }
  if (orderMatter) {
    return fp.isEqual(arr1, arr2);
  }
  return arr1.filter((e) => arr2.includes(e)).length === arr2.length;
};

export const convertHexa = fp.parseInt(16);

export const getId = fp.map(({ id }) => id);

export const sortBySequence = fp.orderBy(fp.get('sequence'), 'asc');

export const convertFilesForInitialValues = (files) => {
  if (isEmpty(files)) return [];
  // convert to array
  const arrFiles = fp.isArray(files) ? files : [files];

  return arrFiles.map(({ id, filename }) => ({
    id,
    uid: id,
    name: filename,
    status: 'done',
    url: `${downloadUrl}${id}`,
  }));
};

export const formatDatePickerValue = (
  value,
  { format } = { format: 'YYYY-MM-DD' }
) => {
  return value ? moment(value).format(format) : void 0;
};

export const findDuplicateByKey = (key) =>
  fp.pipe(
    fp.groupBy(key),
    fp.find((key) => fp.size(key) > 1)
  );

export const getMin = (key) =>
  fp.pipe(
    fp.groupBy(key),
    fp.keys,
    fp.map((o) => parseInt(o, 10)),
    fp.min
  );

export const getMax = (key) =>
  fp.pipe(
    fp.groupBy(key),
    fp.keys,
    fp.map((o) => parseInt(o, 10)),
    fp.max
  );

export const getValueEnumFromData = (alias, format = true) =>
  fp.pipe(
    fp.get(`${alias}.enumValues`),
    fp.reduce((acc, { name }) => {
      acc[name] = format ? formatEnum(name) : name;
      return acc;
    }, {})
  );

export const formatEnum = fp.pipe(fp.lowerCase, fp.upperFirst);

export const containsOneOf = (values) => (array) => {
  return values.some((value) => array?.indexOf(value) >= 0);
};

export const isFacultyAndDepartmentMatch = ({
  selectedFaculty,
  selectedDepartment,
  faculties,
}) => {
  if (!selectedDepartment) return true;
  if (!selectedFaculty) return false;
  const faculty = faculties.find(({ id }) => id === selectedFaculty);
  return (
    faculty?.departments?.some(({ id }) => id === selectedDepartment) ?? false
  );
};

export const convertArrayToValueEnum = ({ idKey, valueKey }) =>
  fp.reduce((acc, cur) => {
    const id = cur[idKey];
    const value = cur[valueKey];
    acc[id] = value;
    return acc;
  }, {});

export const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
export const displayFacDept = ({ department, faculty, prefix = ' - ' }) => {
  if (prefix) {
    return department
      ? `${prefix}${department.name}`
      : faculty
      ? `${prefix}${faculty.name}`
      : '';
  }

  return department ? `${department.name}` : faculty ? `${faculty.name}` : '';
};

/**
 * To filter ant pro table by status
 * @param {string} status - status to be searched
 * @returns
 */
export const filterByStatus = (status) =>
  fp.filter((item) => {
    if (!status || status === 'ALL') {
      return true;
    }
    if (status === 'ACTIVE') {
      return (
        ['DRAFT', 'PENDING', 'APPROVED', 'WITHDRAWN'].indexOf(item.status) > -1
      );
    }
    return item.status === status;
  });

/**
 * To filter ant pro table by status
 * @param {string} status - status to be searched
 * @returns
 */
export const filterByReportType = (reportType) =>
  fp.filter(
    (item) =>
      item.reportType === reportType || !reportType || reportType === 'ALL'
  );

/**
 * To filter ant pro table by project status
 * @param {string} projectStatusId - project status id to be searched
 * @returns
 */
export const filterByProjectStatus = (projectStatus) =>
  fp.filter(
    (item) =>
      item?.project?.projectStatus?.controlType === projectStatus ||
      !projectStatus ||
      (projectStatus === 'ALL' &&
        item?.project?.projectStatus.controlType !==
          ENUM_PROJECT_STATUS_CONTROL_TYPE.PREPARATION)
  );

/**
 * To filter ant pro table by looking for keyword in project title & project code
 * @param {string} keyword - keyword to be searched
 * @returns
 */
export const filterByKeyword = (keyword) =>
  fp.filter(
    (item) =>
      !keyword ||
      item.project?.title?.toLowerCase().includes(keyword) ||
      item.project?.profile?.projectCode?.includes(keyword)
  );

/**
 * To filter ant pro table by looking for keyword in title
 * @param {string} keyword - keyword to be searched
 * @returns
 */
export const filterByTitle = (keyword) =>
  fp.filter((item) => !keyword || item.title?.toLowerCase().includes(keyword));

/**
 * To filter ant pro table by looking for funding id
 * @param {string} funding - funding ID to be searched
 * @returns
 */
export const filterByFunding = (funding) =>
  fp.filter((item) => !funding || item?.setting?.id === funding);

/**
 * To filter ant pro table by date in dateField
 * @param {date} date - date to be searched
 * @param {string} dateField - name of the date field to be searching from
 * @returns
 */
export const filterBydate = ({ date, dateField }) =>
  fp.filter(
    (item) => !date || moment(item[dateField]).isBetween(date[0], date[1])
  );

/**
 * To filter the pending approval table by "isCurrentAppointedApprover"
 * @param {string} activeKey - mine or all
 * @returns
 */
export const filterByActiveKey = (activeKey) =>
  fp.filter((item) =>
    activeKey === 'mine'
      ? item.isCurrentAppointedApprover ||
        item.isCurrentAppointedReviewer ||
        item.isCurrentAppointedEndorser
      : true
  );

export const displayRole = (UserRole) => {
  const { role, department, faculty } = UserRole;
  if (department) return `${role.name} - ${department.name}`;
  if (faculty) return `${role.name} - ${faculty.name}`;
  return `${role.name}`;
};

export const displayAssistantOf = (UserRole) => {
  const { passFrom } = UserRole;
  if (passFrom)
    return `As an assistant of ${passFrom.title ? passFrom.title + ' ' : ''}${
      passFrom.name
    }`;
};
